import React from "react"
import { WorkflowNode, WorkflowNodeData } from "lib/types/workflow"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSideBarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import { DeleteNodeButton, ReadOnlyFieldset } from "./NodeEditorSidebar.styled"
import TriggerNodeForm from "./TriggerNodeForm"
import ConditionNodeForm from "./ConditionNodeForm"
import ActionNodeForm from "./ActionNodeForm"
import BranchNodeForm from "./BranchNodeForm"
import {
  NODE_TYPE_ACTION,
  NODE_TYPE_BRANCH,
  NODE_TYPE_CONDITION,
  NODE_TYPE_TRIGGER
} from "../nodeKind"

type Props = {
  selectedNode: WorkflowNode | null
  onSave: (nodeId: string, data: WorkflowNodeData) => void
  onDelete: (nodeId: string) => void
  /** Optional case id to autofocus when a BRANCH node is open — used after
   *  the inline "+" on the canvas appends a new case so the user lands
   *  directly on the empty Case name field. */
  autoFocusCaseId?: string
  readOnly?: boolean
}

const NodeEditorSidebar = ({
  selectedNode,
  onSave,
  onDelete,
  autoFocusCaseId,
  readOnly = false
}: Props) => {
  const dispatch = useAppDispatch()

  if (!selectedNode) return null

  const nodeType = selectedNode.data.nodeType
  const TITLES: Record<string, { read: string; edit: string }> = {
    [NODE_TYPE_TRIGGER]: { read: "Trigger", edit: "Edit Trigger" },
    [NODE_TYPE_CONDITION]: { read: "Condition", edit: "Edit Condition" },
    [NODE_TYPE_BRANCH]: { read: "Branch", edit: "Edit Branch" },
    [NODE_TYPE_ACTION]: { read: "Action", edit: "Edit Action" }
  }
  const titleEntry = TITLES[nodeType] ?? TITLES[NODE_TYPE_ACTION]
  const title = readOnly ? titleEntry.read : titleEntry.edit

  const handleClose = () => {
    dispatch(hideEditSidebar())
  }

  const form = (
    <>
      {nodeType === NODE_TYPE_TRIGGER && (
        <TriggerNodeForm node={selectedNode} onSave={onSave} hideSave={readOnly} />
      )}
      {nodeType === NODE_TYPE_CONDITION && (
        <ConditionNodeForm node={selectedNode} onSave={onSave} hideSave={readOnly} />
      )}
      {nodeType === NODE_TYPE_BRANCH && (
        <BranchNodeForm
          node={selectedNode}
          onSave={onSave}
          hideSave={readOnly}
          autoFocusCaseId={autoFocusCaseId}
        />
      )}
      {nodeType === NODE_TYPE_ACTION && (
        <ActionNodeForm node={selectedNode} onSave={onSave} hideSave={readOnly} />
      )}
    </>
  )

  return (
    <EditSidebar cancelEvent={handleClose}>
      <EditSideBarHeader title={title} cancelEvent={handleClose}>
        {!readOnly && nodeType !== NODE_TYPE_TRIGGER && (
          <DeleteNodeButton
            type="button"
            onClick={() => {
              onDelete(selectedNode.id)
              handleClose()
            }}
          >
            Delete Node
          </DeleteNodeButton>
        )}
      </EditSideBarHeader>
      {readOnly ? <ReadOnlyFieldset disabled>{form}</ReadOnlyFieldset> : form}
    </EditSidebar>
  )
}

export default NodeEditorSidebar
